import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AuthCallback } from "@/components/AuthCallback";
import { PublicLayout } from "@/components/PublicLayout";
import { BookingProvider } from "@/components/booking/BookingDialog";

import Home from "@/pages/Home";
import Programs from "@/pages/Programs";
import Coaching from "@/pages/Coaching";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Vlog from "@/pages/Vlog";
import Contact from "@/pages/Contact";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

import DashboardLayout from "@/pages/dashboard/DashboardLayout";
import Overview from "@/pages/dashboard/Overview";
import Bookings from "@/pages/dashboard/Bookings";
import Progress from "@/pages/dashboard/Progress";
import MembershipStatus from "@/pages/dashboard/MembershipStatus";
import AdminLayout from "@/pages/dashboard/admin/AdminLayout";
import AdminBlog from "@/pages/dashboard/admin/AdminBlog";
import AdminVlog from "@/pages/dashboard/admin/AdminVlog";
import AdminFounders from "@/pages/dashboard/admin/AdminFounders";
import AdminClasses from "@/pages/dashboard/admin/AdminClasses";
import AdminFoundingMembers from "@/pages/dashboard/admin/AdminFoundingMembers";
import AdminEnquiries from "@/pages/dashboard/admin/AdminEnquiries";

// Links like "/#timetable" scroll to that section after the page renders.
function HashScroll() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  return null;
}

function GoogleCallbackGate({ children }) {
  const { hash } = useLocation();
  if (hash?.includes("session_id=")) return <AuthCallback />;
  return children;
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let rafId;
    function raf(time) {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    }
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      <div className="grain-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <BookingProvider>
          <HashScroll />
          <GoogleCallbackGate>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="/programs" element={<Programs />} />
              <Route path="/schedule" element={<Navigate to="/#timetable" replace />} />
              <Route path="/membership" element={<Navigate to="/#timetable" replace />} />
              <Route path="/coaching" element={<Coaching />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/vlog" element={<Vlog />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
            </Route>
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Overview />} />
              <Route path="bookings" element={<Bookings />} />
              <Route path="progress" element={<Progress />} />
              <Route path="membership" element={<MembershipStatus />} />
              <Route path="admin" element={<AdminLayout />}>
                <Route index element={<AdminEnquiries />} />
                <Route path="enquiries" element={<AdminEnquiries />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="vlog" element={<AdminVlog />} />
                <Route path="founders" element={<AdminFounders />} />
                <Route path="classes" element={<AdminClasses />} />
                <Route path="founding-members" element={<AdminFoundingMembers />} />
              </Route>
            </Route>
          </Routes>
          </GoogleCallbackGate>
          </BookingProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" theme="dark" />
    </div>
  );
}

export default App;
